import type { DesktopUpdateActionResult } from "@cleo/contracts";

import { ensureLocalApi } from "../localApi";
import {
  getDesktopUpdateInstallConfirmationMessage,
  type DesktopUpdateButtonAction,
} from "./desktopUpdate.logic";

/**
 * What Update does, from the notice and from the button left of the bell
 * (Can 30). A Desk with `updateNow` takes the one click: it downloads the
 * release if needed, waits until no Cleo turn runs, then installs and reopens.
 * Nothing running is interrupted, so there is no confirmation. An older Desk
 * keeps its two steps: `step` downloads, or installs behind the app's
 * confirmation. Resolves with the Desk's answer, or null when nothing was asked
 * of it (no bridge, nothing to do, or the confirmation was declined).
 */
export async function updateDesktopNow(
  step: DesktopUpdateButtonAction,
  version: string | null,
): Promise<DesktopUpdateActionResult | null> {
  const bridge = window.desktopBridge;
  if (!bridge) return null;
  if (bridge.updateNow) return bridge.updateNow();
  if (step === "download") return bridge.downloadUpdate();
  if (step !== "install") return null;
  const confirmed = await ensureLocalApi().dialogs.confirm(
    getDesktopUpdateInstallConfirmationMessage(
      { availableVersion: version, downloadedVersion: version },
      navigator.platform,
    ),
  );
  return confirmed ? bridge.installUpdate() : null;
}

/** Whether this Desk takes the one click, or still downloads and installs as two steps. */
export function canUpdateDesktopNow(): boolean {
  return typeof window !== "undefined" && typeof window.desktopBridge?.updateNow === "function";
}
